import { asImageSrc } from "@prismicio/client"
import React from "react"

import { DEFAULT_LANGUAGE, useTranslation } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"
import { StoreTagType } from "@bounce/store"

import { StructuredData } from "./components/StructuredData"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { useHero } from "./hooks/useHero"
import { useInterpolations } from "./hooks/useInterpolations"
import { useSEO } from "./hooks/useSEO"
import { ReviewsSlider } from "@/components/Generic/Reviews/ReviewsSlider/ReviewsSlider"
import { RichTextSection } from "@/components/Generic/RichText/RichTextSection/RichTextSection"
import { HeroCityPoi } from "@/components/LuggageStorage/HeroCityPoi/HeroCityPoi"
import { HowItWorksCompact } from "@/components/LuggageStorage/HowItWorks/HowItWorksCompact/HowItWorksCompact"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import { PoiChipNavSlider } from "@/components/LuggageStorage/PoiChipNavSlider/PoiChipNavSlider"
import { useSearchBar } from "@/components/LuggageStorage/SearchBar/useSearchBar"
import { StoreGridSection } from "@/components/LuggageStorage/StoreSlider/StoreGridSection"
import StoreSliderSection from "@/components/LuggageStorage/StoreSlider/StoreSliderSection"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import type { AggregateRating } from "@/libs/bounce/aggregateRating"
import type { PoiData } from "@/libs/bounce/pois"
import type { NearbyStorageSpot, TopRatedPoiStore } from "@/libs/bounce/stores/types"
import { usePoiUrl } from "@/libs/router"
import { StoreList } from "@/screens/LuggageStorage/PoiScreen/components/StoreList"
import { useStepsCompact } from "@/screens/LuggageStorage/PoiScreen/hooks/useStepsCompact"
import type { Review } from "@/utils/reviews"
import { removeContentWithWords } from "@/utils/richText/filters"

export type PoiScreenProps = {
  aggregateRating: AggregateRating
  nearbyLockers: NearbyStorageSpot[]
  nearbyStores: NearbyStorageSpot[]
  poi: PoiData
  reviews: Review[]
  topRatedNearbyStores: TopRatedPoiStore[]
}

const PoiScreen = ({
  aggregateRating,
  nearbyLockers,
  nearbyStores: allNearestStores,
  poi,
  reviews,
  topRatedNearbyStores,
}: PoiScreenProps) => {
  const { getUrl: getPoiUrl, absolutePath } = usePoiUrl({ poi, city: poi.city })
  const interpolations = useInterpolations({ poi })
  const { metaTitle, metaDescription } = useSEO({ poi, interpolations })
  const breadcrumbs = useBreadcrumbs({ poi })
  const heroProps = useHero({ poi, interpolations, aggregateRating })
  const { searchUrl } = useSearchBar({ initialQuery: heroProps.query, from: "POI" })
  const { t } = useTranslation("marketing-ls-poi")
  const { coupon } = useReferrals.useCodes()
  const stepsCompact = useStepsCompact({ interpolations })

  const alternates = poi.alternateLanguages
    .map(language => ({ language, href: getPoiUrl({ absolute: true, language }) }))
    .concat([{ language: "x-default", href: getPoiUrl({ absolute: true, language: DEFAULT_LANGUAGE }) }])

  const openAllTimeNearestStores = allNearestStores.filter(
    ({ tags }) => tags?.some(({ key }) => key === StoreTagType.OpenAllTime),
  )
  const nearestStores = allNearestStores.filter(
    ({ tags }) => !tags?.some(({ key }) => key === StoreTagType.OpenAllTime),
  )
  // To guarantee that the first store inventory display is in grid format
  const NearestStoresComponent = openAllTimeNearestStores.length > 0 ? StoreSliderSection : StoreGridSection

  const otherPois = poi.city.pois?.filter(({ id }) => id !== poi.id)

  const filteredContent = removeContentWithWords(poi.content, ["Bounce"])

  return (
    <StickyCTAProvider config={{ searchQuery: poi.query, from: "Poi", coupon, label: heroProps.cta }}>
      <Layout
        alternates={alternates}
        headProps={{
          title: metaTitle,
          description: metaDescription,
          url: absolutePath,
          image: asImageSrc(poi.image, { ar: "1.91:1", fit: "crop" }),
          noIndex: true,
        }}
        languages={poi.alternateLanguages}
        from="POI"
        searchQuery={poi.query}
        breadcrumbs={breadcrumbs}
        hideNewsletterBanner
        hideDynamicAppBanner>
        <PoiChipNavSlider pois={otherPois} nearMeSearchUrl={searchUrl} from="POI" />
        <HeroCityPoi
          {...heroProps}
          coordinates={poi.coordinates}
          nearestStores={allNearestStores}
          className="max-w-full"
          searchUrl={searchUrl}
        />
        {!!topRatedNearbyStores.length && (
          <StoreList
            stores={topRatedNearbyStores}
            from="POI"
            path={absolutePath}
            className="pt-section"
            searchQuery={poi.query}
            title={t(
              "screen.placeScreen.topRatedNearestStoresSlider.titleV3",
              "Top-rated places to store your luggage around {PLACE_NAME}",
              { PLACE_NAME: interpolations.PLACE_NAME },
            )}
            subtitle={t(
              "screen.placeScreen.topRatedNearestStoresSlider.subtitleV3",
              "Keep your bags safe at one of our top-rated luggage storage spots",
            )}
          />
        )}
        {nearestStores.length > 0 && (
          <NearestStoresComponent
            name="NearestStores"
            stores={nearestStores}
            from="POI"
            className="pt-section"
            lowestPrice={interpolations.LOWEST_PRICE}
            featuredPoi={poi}
            path={absolutePath}
            searchQuery={poi.query}
            title={t("screen.placeScreen.nearestStoresSlider.titleV7", "Need a storage spot near {PLACE_NAME}?", {
              PLACE_NAME: interpolations.PLACE_NAME,
            })}
            subtitle={t(
              "screen.placeScreen.nearestStoresSlider.subtitleV2",
              "{count, plural, one {Choose the most convenient luggage storage location} other {Choose from # convenient luggage storage locations}} near {PLACE_NAME}",
              {
                count: nearestStores.length,
                PLACE_NAME: interpolations.PLACE_NAME,
              },
            )}
            renderStructuredData={!topRatedNearbyStores.length}
          />
        )}
        {openAllTimeNearestStores.length > 0 && (
          <StoreGridSection
            className="pt-section"
            from="POI"
            featuredPoi={poi}
            path={absolutePath}
            searchQuery={poi.query}
            stores={openAllTimeNearestStores}
            title={t(
              "screen.placeScreen.openAllTimeNearestStoresSlider.titleOptimized",
              "24/7 storage spots at {{PLACE_NAME}}",
              { PLACE_NAME: interpolations.PLACE_NAME },
            )}
            subtitle={t(
              "screen.placeScreen.openAllTimeNearestStoresSlider.subtitleOptimizedV2",
              "Make the most of your time in {{CITY_NAME}} with 24 hour bag storage from {{PRICE}}/day",
              {
                CITY_NAME: interpolations.CITY_NAME,
                PRICE: interpolations.PRICE,
              },
            )}
            renderStructuredData={false}
          />
        )}
        <HowItWorksCompact
          className="pt-section"
          title={t(
            "screen.placeScreen.howItWorks.titleSYB",
            "How to Bounce your luggage at {{PLACE_NAME}}",
            interpolations,
          )}
          steps={stepsCompact}
        />
        {nearbyLockers.length > 0 && (
          <StoreSliderSection
            name="NearbyLockers"
            className="pt-section"
            from="POI"
            lowestPrice={interpolations.LOWEST_PRICE}
            path={absolutePath}
            featuredPoi={poi}
            searchQuery={poi.query}
            stores={nearbyLockers}
            subtitle={t(
              "screen.placeScreen.nearbyLockersSlider.subtitleV3",
              "Find secure and convenient lockers around {{PLACE_NAME}}",
              { PLACE_NAME: interpolations.PLACE_NAME },
            )}
            title={t("screen.placeScreen.nearbyLockersSlider.titleV2", "Store your bags in lockers near {PLACE_NAME}", {
              PLACE_NAME: interpolations.PLACE_NAME,
            })}
            renderStructuredData={false}
            sectionViewedMeta={{ section: "PoiNearbyLockers" }}
          />
        )}
        <ReviewsSlider
          cityId={poi.city.id}
          className="pt-section"
          name="ReviewsSliderPoiScreen"
          reviews={reviews}
          title={t("screen.placeScreen.reviews.titleV4", "Most helpful reviews in {PLACE_NAME}", {
            PLACE_NAME: interpolations.PLACE_NAME,
          })}
          hideReviewsModal={true}
          disableDefaultReviewsFallback={true}
        />
        {filteredContent && (
          <RichTextSection
            field={filteredContent}
            className="py-section"
            interpolations={interpolations}
            data-nosnippet
            hidePublicationDate
          />
        )}
        <StructuredData poi={poi} aggregateRating={aggregateRating} reviews={reviews} />
      </Layout>
    </StickyCTAProvider>
  )
}

export default PoiScreen
